/**
 * Calendar-date ranges for report filters. Dates are plain "YYYY-MM-DD"
 * strings in the browser's LOCAL calendar (never `toISOString()`, which is
 * UTC and shifts the day for anyone west of Greenwich in the evening). An
 * empty string means "open-ended" on that side; both empty is all time.
 */
export interface DateRange {
  from: string;
  to: string;
}

export interface DateRangePreset {
  id: string;
  label: string;
  range: DateRange;
}

const pad = (n: number): string => String(n).padStart(2, "0");

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight for a YYYY-MM-DD string (null if it isn't one). */
export function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toIsoDate(d) === iso ? d : null;
}

function addDays(iso: string, days: number): string {
  const d = parseIsoDate(iso)!;
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

function monthRange(year: number, month0: number): DateRange {
  return { from: toIsoDate(new Date(year, month0, 1)), to: toIsoDate(new Date(year, month0 + 1, 0)) };
}

export function buildPresets(today: Date = new Date()): DateRangePreset[] {
  const t = toIsoDate(today);
  const y = today.getFullYear();
  const m = today.getMonth();
  return [
    { id: "today", label: "Today", range: { from: t, to: t } },
    { id: "yesterday", label: "Yesterday", range: { from: addDays(t, -1), to: addDays(t, -1) } },
    { id: "last7", label: "Last 7 days", range: { from: addDays(t, -6), to: t } },
    { id: "thisMonth", label: "This month", range: monthRange(y, m) },
    { id: "lastMonth", label: "Last month", range: monthRange(y, m - 1) },
    { id: "thisYear", label: "This year", range: { from: `${y}-01-01`, to: `${y}-12-31` } },
    { id: "all", label: "All time", range: { from: "", to: "" } },
  ];
}

export function sameRange(a: DateRange, b: DateRange): boolean {
  return a.from === b.from && a.to === b.to;
}

/**
 * The previous/next window of the same kind: one day for a single day, one
 * month for a whole calendar month, one year for a whole calendar year,
 * otherwise the same number of days. Null when the range is open-ended.
 */
export function stepRange(range: DateRange, direction: -1 | 1): DateRange | null {
  const from = parseIsoDate(range.from);
  const to = parseIsoDate(range.to);
  if (!from || !to) return null;

  if (range.from === range.to) {
    const day = addDays(range.from, direction);
    return { from: day, to: day };
  }
  const wholeMonth = sameRange(range, monthRange(from.getFullYear(), from.getMonth()));
  if (wholeMonth) return monthRange(from.getFullYear(), from.getMonth() + direction);
  if (range.from === `${from.getFullYear()}-01-01` && range.to === `${from.getFullYear()}-12-31`) {
    const y = from.getFullYear() + direction;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  const span = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  return { from: addDays(range.from, direction * span), to: addDays(range.to, direction * span) };
}

const shortDate = (iso: string, withYear: boolean): string =>
  parseIsoDate(iso)!.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });

/** "All time" / "Sep 26, 2026" / "Sep 1 – Sep 26, 2026" / "Since Sep 1, 2026". */
export function describeRange(range: DateRange): string {
  const { from, to } = range;
  if (!from && !to) return "All time";
  if (from && !to) return `Since ${shortDate(from, true)}`;
  if (!from && to) return `Through ${shortDate(to, true)}`;
  if (from === to) return shortDate(from, true);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${shortDate(from, !sameYear)} – ${shortDate(to, true)}`;
}
